/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * GIAO DIỆN — chế độ SÁNG/TỐI và BỘ CHỮ. (`GD-02`, lee duyệt mẫu 11/09.)
 *
 * 🔴 HAI CÂU HỎI ĐỘC LẬP, và tệp này giữ chúng độc lập:
 *
 *     cheDo  'toi' | 'sang'          -> doi BANG MAU   (thietke.css, [data-che-do])
 *     boChu  'lexend' | 'bevietnam'  -> doi CHU THUONG (--font-chu)
 *
 *   Gộp chúng thành một "theme" bốn trạng thái là bắt người dùng đổi cả hai khi họ chỉ muốn
 *   đổi một — và là bắt mã phải nhớ bốn ca thay vì hai lần hai.
 *
 * ⛔ NGUỒN SỰ THẬT LÀ THUỘC TÍNH TRÊN `<html>`, không phải state của React.
 *   `thietke.css` đọc `:root[data-che-do="sang"]`, và Tailwind đọc `var(--font-chu)` — cả hai
 *   là CSS thuần. React chỉ có mỗi việc **ghi** thuộc tính ấy. Nghĩa là: một trang chưa kịp
 *   render, một cổng thành phần chưa mount, một `<iframe>` — tất cả vẫn đúng màu.
 *
 * ⚠ VÀ VÌ THẾ NÓ PHẢI GHI TRONG `useEffect`, không phải lúc dựng state: ghi vào DOM giữa lúc
 *   React đang tính toán là một tác dụng phụ trong pha render — React 18 chạy pha ấy hai lần
 *   ở chế độ nghiêm ngặt, và những thứ như thế bắt đầu nhấp nháy.
 *
 * 🔴 `localStorage` BỌC `try/catch` — cửa sổ riêng tư / trình duyệt chặn lưu trữ thì nó NÉM,
 *   và **một tuỳ chọn hiển thị không được phép làm sập cả app**. Cùng luật với `KHOA_CAY` ở
 *   `Layout.tsx` và với khoá nhớ cây tài khoản ở `QuanTri.tsx`.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type CheDo = 'toi' | 'sang';
export type BoChu = 'lexend' | 'bevietnam';

/**
 * Tên phông THẬT của từng lựa chọn — một bảng, không phải một chuỗi `if`.
 *
 * ⚠ Tên ở đây phải khớp **từng chữ** với tên họ phông mà `@fontsource/*` đăng ký
 *   (`main.tsx` nhập các tệp CSS ấy). Gõ sai một chữ thì trình duyệt im lặng rơi về
 *   `Segoe UI` — chữ vẫn đẹp, và không ai biết là đã hỏng.
 */
export const TEN_BO_CHU: Record<BoChu, string> = {
  lexend: 'Lexend',
  bevietnam: 'Be Vietnam Pro',
};

/** Nhãn cho menu — một chỗ viết, một chỗ đọc. */
export const NHAN_BO_CHU: Record<BoChu, string> = {
  lexend: 'Lexend',
  bevietnam: 'Be Vietnam Pro',
};

export const NHAN_CHE_DO: Record<CheDo, string> = {
  toi: 'Tối',
  sang: 'Sáng',
};

/** Chữ TIÊU ĐỀ không đổi theo lựa chọn — lee chốt một bộ có chân duy nhất. */
const FONT_TIEU_DE = 'Playfair Display';

const KHOA = 'kapp.giaodien';

interface GiaoDienValue {
  cheDo: CheDo;
  boChu: BoChu;
  datCheDo: (v: CheDo) => void;
  datBoChu: (v: BoChu) => void;
  /** Đảo tối ⇄ sáng — cho cái công tắc một chạm ở menu người dùng. */
  daoCheDo: () => void;
}

const Ctx = createContext<GiaoDienValue | null>(null);

/**
 * Đọc lựa chọn đã lưu. Hỏng/thiếu/lạ ⇒ **mặc định**, không ném.
 *
 * ⛔ KIỂM TỪNG GIÁ TRỊ chứ không tin `JSON.parse`: chuỗi trong `localStorage` là thứ người
 *   dùng (hoặc một bản app cũ hơn) ghi được. Một `cheDo: 'xanh-la'` lọt vào thì
 *   `data-che-do="xanh-la"` không khớp bộ nào và cả app rơi về bảng TỐI — trong khi người
 *   dùng thấy công tắc đang ở "Sáng". Hai chỗ nói hai điều khác nhau, và không chỗ nào sai
 *   một cách nhìn thấy được.
 */
function doc(): { cheDo: CheDo; boChu: BoChu } {
  const macDinh = { cheDo: 'toi' as CheDo, boChu: 'lexend' as BoChu };
  try {
    const raw = localStorage.getItem(KHOA);
    if (!raw) return macDinh;
    const o = JSON.parse(raw) as Partial<{ cheDo: string; boChu: string }>;
    return {
      cheDo: o.cheDo === 'sang' || o.cheDo === 'toi' ? o.cheDo : macDinh.cheDo,
      boChu: o.boChu === 'bevietnam' || o.boChu === 'lexend' ? o.boChu : macDinh.boChu,
    };
  } catch {
    return macDinh;
  }
}

export function GiaoDienProvider({ children }: { children: ReactNode }) {
  const [cheDo, setCheDo] = useState<CheDo>(() => doc().cheDo);
  const [boChu, setBoChu] = useState<BoChu>(() => doc().boChu);

  /*
   * ⛔ MỘT `useEffect` CHO CẢ HAI, và nó ghi **cả ba thứ** mỗi lần: thuộc tính chế độ, biến
   *   chữ thường, biến chữ tiêu đề. Tách ra ba effect thì có ba thứ tự chạy, và thứ tự ấy đổi
   *   theo cách React gom cập nhật — thứ không ai muốn phụ thuộc vào.
   */
  useEffect(() => {
    const g = document.documentElement;
    g.dataset.cheDo = cheDo;
    /*
     * ⚠ DẤU NHÁY ĐƠN QUANH TÊN PHÔNG là bắt buộc: `Be Vietnam Pro` có khoảng trắng, và một
     *   `font-family: Be Vietnam Pro` không nháy là CSS **hỏng** — trình duyệt bỏ cả dòng và
     *   rơi về phông dự phòng. Lexend không có khoảng trắng nên nó chạy được mà không nháy;
     *   đó đúng là kiểu lỗi chỉ hiện ra với MỘT trong hai lựa chọn.
     */
    g.style.setProperty('--font-chu', `'${TEN_BO_CHU[boChu]}'`);
    g.style.setProperty('--font-tieude', `'${FONT_TIEU_DE}'`);
    try {
      localStorage.setItem(KHOA, JSON.stringify({ cheDo, boChu }));
    } catch { /* riêng tư / đầy — không phải lỗi đáng chặn */ }
  }, [cheDo, boChu]);

  const value = useMemo<GiaoDienValue>(() => ({
    cheDo,
    boChu,
    datCheDo: setCheDo,
    datBoChu: setBoChu,
    daoCheDo: () => setCheDo((v) => (v === 'toi' ? 'sang' : 'toi')),
  }), [cheDo, boChu]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * ⛔ NÉM khi gọi ngoài `GiaoDienProvider`, không trả một giá trị mặc định êm ái.
 *   Một `useGiaoDien()` lặng lẽ trả `'toi'` ở ngoài cây là một công tắc bấm không ăn — và
 *   người sửa sẽ đi tìm ở CSS. Cùng luật với `useAuth()`.
 */
export function useGiaoDien(): GiaoDienValue {
  const c = useContext(Ctx);
  if (!c) throw new Error('useGiaoDien phải nằm trong <GiaoDienProvider>');
  return c;
}
